import React, { useEffect, useState, useContext } from "react";
import { Button, Table, Modal, Form, Spinner, Alert, Badge } from "react-bootstrap";
import { AuthContext } from "../../hooks/auth/AuthContext";
import Head from "../../components/layout/head/Head";
import { toast } from "react-toastify";
import api from "../../services/api";

const TimbradosPage = () => {
    const authContext = useContext(AuthContext);
    const { user } = authContext;

    const [timbrados, setTimbrados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [incluirInactivos, setIncluirInactivos] = useState(false);
    const [soloVigentes, setSoloVigentes] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        id: "",
        numero: "",
        fechaInicio: "",
        fechaFin: "",
        idCsc: "",
        csc: "",
        activo: true,
    });

    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [incluirInactivos, soloVigentes]);

    const loadData = async () => {
        try {
            setLoading(true);
            const config = {
                headers: { 'Authorization': `Bearer ${user.token}` },
                params: { 
                    soloActivos: !incluirInactivos,
                    soloVigentes: soloVigentes
                }
            };
            const response = await api.get("/api/timbrados", config);
            setTimbrados(response.data);
        } catch (error) {
            console.error("Error al cargar timbrados:", error);
            toast.error("Error al cargar los timbrados");
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = async (item = null) => {
        if (item) {
            setEditMode(true);
            setCurrentItem(item);
            setFormData({
                id: item.id,
                numero: item.numero || "",
                fechaInicio: item.fechaInicio ? item.fechaInicio.split('T')[0] : "",
                fechaFin: item.fechaFin ? item.fechaFin.split('T')[0] : "",
                idCsc: item.idCsc || "",
                csc: item.csc || "",
                activo: item.activo !== undefined ? item.activo : true,
            });
        } else {
            setEditMode(false);
            setCurrentItem(null);
            setFormData({
                id: "",
                numero: "",
                fechaInicio: "",
                fechaFin: "",
                idCsc: "",
                csc: "",
                activo: true,
            });
        }
        setFormErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.numero || formData.numero.trim() === "") {
            errors.numero = "El número de timbrado es obligatorio";
        }

        if (!formData.fechaInicio) {
            errors.fechaInicio = "La fecha de inicio es obligatoria";
        }

        if (!formData.fechaFin) {
            errors.fechaFin = "La fecha de fin es obligatoria";
        }

        if (formData.fechaInicio && formData.fechaFin) {
            const inicio = new Date(formData.fechaInicio);
            const fin = new Date(formData.fechaFin);
            if (fin <= inicio) {
                errors.fechaFin = "La fecha de fin debe ser posterior a la fecha de inicio";
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const config = {
                headers: { 'Authorization': `Bearer ${user.token}` }
            };

            const dataToSend = {
                Numero: formData.numero,
                FechaInicio: formData.fechaInicio,
                FechaFin: formData.fechaFin,
                IdCsc: formData.idCsc || null,
                Csc: formData.csc || null,
                Activo: formData.activo,
            };

            if (editMode) {
                dataToSend.Id = formData.id;
                await api.put(`/api/timbrados/${formData.id}`, dataToSend, config);
                toast.success("Timbrado actualizado correctamente");
            } else {
                await api.post("/api/timbrados", dataToSend, config);
                toast.success("Timbrado creado correctamente");
            }

            handleCloseModal();
            loadData();
        } catch (error) {
            console.error("Error al guardar timbrado:", error);
            if (error.response?.data) {
                toast.error(typeof error.response.data === 'string' ? error.response.data : "Error al guardar el timbrado");
            } else {
                toast.error("Error al guardar el timbrado");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const isVigente = (fechaInicio, fechaFin) => {
        const hoy = new Date();
        const inicio = new Date(fechaInicio);
        const fin = new Date(fechaFin);
        return hoy >= inicio && hoy <= fin;
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <>
            <Head title="Configuración - Timbrados" />
            <div className="container-fluid mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2>Gestión de Timbrados</h2>
                    <div className="d-flex gap-3 align-items-center">
                        <Form.Check
                            type="checkbox"
                            label="Solo vigentes"
                            checked={soloVigentes}
                            onChange={(e) => setSoloVigentes(e.target.checked)}
                        />
                        <Form.Check
                            type="checkbox"
                            label="Incluir inactivos"
                            checked={incluirInactivos}
                            onChange={(e) => setIncluirInactivos(e.target.checked)}
                        />
                        <Button variant="primary" onClick={() => handleShowModal()}>
                            Nuevo Timbrado
                        </Button>
                    </div>
                </div>

                <Alert variant="info" className="mb-3">
                    <strong>Información:</strong> Los timbrados son autorizaciones de la SET para emitir documentos electrónicos. 
                    Cada timbrado tiene un número único y un rango de vigencia.
                </Alert>

                {timbrados.length === 0 ? (
                    <Alert variant="warning">No hay timbrados registrados.</Alert>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Número</th>
                                    <th>Fecha Inicio</th>
                                    <th>Fecha Fin</th>
                                    <th>Vigencia</th>
                                    <th>ID CSC</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timbrados.map((item) => (
                                    <tr key={item.id}>
                                        <td><strong>{item.numero}</strong></td>
                                        <td>{new Date(item.fechaInicio).toLocaleDateString()}</td>
                                        <td>{new Date(item.fechaFin).toLocaleDateString()}</td>
                                        <td>
                                            {isVigente(item.fechaInicio, item.fechaFin) ? (
                                                <Badge bg="success">Vigente</Badge>
                                            ) : (
                                                <Badge bg="danger">Vencido</Badge>
                                            )}
                                        </td>
                                        <td>{item.idCsc || <span className="text-muted">N/A</span>}</td>
                                        <td>
                                            <Badge bg={item.activo ? "success" : "secondary"}>
                                                {item.activo ? "Activo" : "Inactivo"}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleShowModal(item)}
                                            >
                                                Editar
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Modal para Crear/Editar */}
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{editMode ? "Editar Timbrado" : "Nuevo Timbrado"}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Número de Timbrado *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="numero"
                                    value={formData.numero}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.numero}
                                    maxLength={10}
                                    placeholder="Ej: 12345678"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.numero}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Fecha Inicio *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="fechaInicio"
                                            value={formData.fechaInicio}
                                            onChange={handleInputChange}
                                            isInvalid={!!formErrors.fechaInicio}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.fechaInicio}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Fecha Fin *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="fechaFin"
                                            value={formData.fechaFin}
                                            onChange={handleInputChange}
                                            isInvalid={!!formErrors.fechaFin}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.fechaFin}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                            </div>

                            <Alert variant="secondary" className="mb-3">
                                <strong>CSC (Código de Seguridad del Contribuyente):</strong> Opcional. 
                                Solo es requerido si la SET lo solicita para su timbrado.
                            </Alert>

                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>ID CSC</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="idCsc"
                                            value={formData.idCsc}
                                            onChange={handleInputChange}
                                            maxLength={10}
                                            placeholder="Opcional"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>CSC</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="csc"
                                            value={formData.csc}
                                            onChange={handleInputChange}
                                            maxLength={256}
                                            placeholder="Opcional"
                                        />
                                    </Form.Group>
                                </div>
                            </div>

                            {editMode && (
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="activo"
                                        label="Activo"
                                        checked={formData.activo}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Guardando...
                                    </>
                                ) : editMode ? (
                                    "Actualizar"
                                ) : (
                                    "Crear"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default TimbradosPage;
